import React from 'react';
import { ConfirmDialog } from '@parsina/framework';

import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { redirectToLogin } from '../../lib/auth.js';
import {
  SessionEvent, isTokenEvent, onSessionEvent, watchSession, extendSession, rememberReturnPath,
} from '../../lib/session.js';

// What a student EXPERIENCES when their session ends. core.md §15.
//
// The mechanics are in src/lib/auth.js and the timers in src/lib/session.js;
// what this adds is the rule that a session ending is an EVENT, not a
// navigation. A full-document redirect from inside the fetch wrapper would
// unmount React and take a half-written SOAP note, a composer draft and a
// half-typed onboarding form with it — so the app STAYS MOUNTED and raises a
// dialog over it. The re-login is something the student chooses, and
// `sessionStorage.return_to` brings them back to the same route.
//
// Two events, two different dialogs:
//
//   expiring — the refresh token is close to its end. The dialog offers to
//              extend, and «ادامه» renews silently: the student never sees a
//              sign-in page for a session that was still good.
//   expired  — it is over. There is no cancel: dismissing it would leave a
//              screen whose every button fails with UNAUTHENTICATED.
//
// The drafts survive either way (localStorage for the SOAP note, sessionStorage
// for a composer), which is what the «returnNotice» line promises.

export default function SessionDialog() {
  const { t } = useTranslation();
  const [event, setEvent] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const watcher = React.useRef(null);

  React.useEffect(() => {
    watcher.current = watchSession();
    // Only the TOKEN events: the bus also carries NOT_ONBOARDED, which App.jsx
    // handles by re-booting — rendering the «نشست به پایان رسید» dialog for it
    // would tell a student with a perfectly good session to sign in again.
    const off = onSessionEvent((next) => { if (isTokenEvent(next)) setEvent(next); });
    return () => {
      off();
      watcher.current?.cancel();
    };
  }, []);

  const stay = async () => {
    setBusy(true);
    const ok = await extendSession();
    setBusy(false);
    if (ok) {
      setEvent(null);
      // A renewed refresh token means a new expiry: the timers have to be
      // re-armed against it, or the next warning never fires.
      watcher.current?.rearm();
      return;
    }
    // The renewal failed, so the session is effectively over — say so instead of
    // leaving a dialog that has quietly stopped working.
    setEvent(SessionEvent.EXPIRED);
  };

  const signIn = () => {
    rememberReturnPath();
    redirectToLogin();
  };

  if (!event) return null;

  if (event === SessionEvent.EXPIRING) {
    return (
      <ConfirmDialog
        open
        onOpenChange={(next) => { if (!next) setEvent(null); }}
        title={t('auth.session.expiringTitle')}
        description={t('auth.session.expiringBody')}
        confirmLabel={t('auth.session.staySignedIn')}
        cancelLabel={t('common.actions.close')}
        loading={busy}
        onConfirm={stay}
        confirmTestId="session-extend"
        data-testid="session-expiring"
      />
    );
  }

  return (
    <ConfirmDialog
      open
      // No dismissal: every request behind this dialog now fails.
      onOpenChange={() => {}}
      title={t('auth.session.expiredTitle')}
      description={`${t('auth.session.expiredBody')} ${t('auth.session.returnNotice')}`}
      confirmLabel={t('auth.session.signInAgain')}
      cancelLabel={t('common.actions.close')}
      onConfirm={signIn}
      confirmTestId="session-signin"
      data-testid="session-expired"
    />
  );
}
